import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MessageCircle, X, Send, AlertCircle, ArrowLeft, User, LogIn } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const formatDay = (dateStr) => {
  const d = new Date(dateStr);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return "Aujourd'hui";
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Hier';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return formatTime(dateStr);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

// ==================== USER CHAT VIEW ====================
const UserChatView = ({ user, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/messages`, { withCredentials: true });
      setMessages(res.data.messages);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchMessages();
    axios.put(`${API}/chat/read`, {}, { withCredentials: true }).catch(() => {});
    const interval = setInterval(() => {
      fetchMessages();
      axios.put(`${API}/chat/read`, {}, { withCredentials: true }).catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    setError(null);
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat/send`, { message: input.trim() }, { withCredentials: true });
      setMessages(prev => [...prev, res.data]);
      setInput('');
    } catch (err) {
      setError(err.response?.status === 401 ? 'Session expirée. Reconnectez-vous.' : "Erreur d'envoi. Réessayez.");
    }
    setSending(false);
  };

  const groupedMessages = messages.reduce((groups, msg) => {
    const day = formatDay(msg.created_at);
    if (!groups.length || groups[groups.length - 1].day !== day) groups.push({ day, messages: [msg] });
    else groups[groups.length - 1].messages.push(msg);
    return groups;
  }, []);

  return (
    <>
      <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#121212]">
        <div className="flex items-center gap-3">
          <img src="https://customer-assets.emergentagent.com/job_fad4c604-2efd-4142-a35f-93aeb2be7f74/artifacts/hzrybxar_logo2.jpg" alt="PG" className="w-9 h-9 rounded-full object-cover" />
          <div>
            <p className="text-white font-semibold text-sm">Phoenix Card Game</p>
            <p className="text-green-400 text-xs">En ligne</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="text-gray-400 hover:text-white h-8 w-8" data-testid="chat-close-button">
          <X className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages-container">
        {messages.length === 0 ? (
          <div className="text-center pt-12">
            <MessageCircle className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 text-sm font-medium">Bienvenue !</p>
            <p className="text-gray-500 text-xs mt-1">Posez-nous vos questions.</p>
          </div>
        ) : groupedMessages.map((group, gi) => (
          <div key={gi}>
            <div className="flex justify-center mb-3">
              <span className="text-[10px] text-gray-600 bg-white/5 px-2 py-0.5 rounded-full">{group.day}</span>
            </div>
            {group.messages.map((msg) => {
              const isMe = msg.sender_id === user.user_id;
              return (
                <div key={msg.message_id} className={`flex mb-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-3 py-2 rounded-2xl ${isMe ? 'bg-fuchsia-600 text-white rounded-br-sm' : 'bg-white/10 text-gray-200 rounded-bl-sm'}`}>
                    {!isMe && <p className="text-fuchsia-400 text-[10px] font-medium mb-0.5">{msg.sender_name}</p>}
                    <p className="text-sm leading-relaxed break-words">{msg.message}</p>
                    <p className={`text-[10px] mt-1 ${isMe ? 'text-white/60' : 'text-gray-500'} text-right`}>{formatTime(msg.created_at)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={handleSend} className="p-3 border-t border-white/10 bg-[#121212]">
        {error && (
          <div className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-red-500/10 border border-red-500/20 rounded-lg">
            <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
            <span className="text-red-400 text-xs">{error}</span>
          </div>
        )}
        <div className="flex gap-2">
          <input ref={inputRef} type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Votre message..." className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-fuchsia-500/50" data-testid="chat-input" />
          <Button type="submit" disabled={!input.trim() || sending} className="bg-fuchsia-600 hover:bg-fuchsia-500 rounded-xl px-3 disabled:opacity-40" data-testid="chat-send-button">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </form>
    </>
  );
};

// ==================== ADMIN CHAT VIEW ====================
const AdminChatView = ({ user, onClose }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/chat/conversations`, { withCredentials: true });
      setConversations(res.data.conversations);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  const openConversation = async (convId) => {
    setSelectedConv(convId);
    try {
      const res = await axios.get(`${API}/admin/chat/messages/${convId}`, { withCredentials: true });
      setMessages(res.data.messages);
      await axios.put(`${API}/admin/chat/read/${convId}`, {}, { withCredentials: true });
      fetchConversations();
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (selectedConv) {
      const interval = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/admin/chat/messages/${selectedConv}`, { withCredentials: true });
          setMessages(res.data.messages);
          await axios.put(`${API}/admin/chat/read/${selectedConv}`, {}, { withCredentials: true });
        } catch { /* ignore */ }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedConv]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { if (selectedConv) inputRef.current?.focus(); }, [selectedConv]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || sending || !selectedConv) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/chat/reply/${selectedConv}`, { message: reply.trim() }, { withCredentials: true });
      setMessages(prev => [...prev, res.data]);
      setReply('');
    } catch { /* ignore */ }
    setSending(false);
  };

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
  const selectedConvInfo = conversations.find(c => c.conversation_id === selectedConv);

  // Conversations list
  if (!selectedConv) {
    return (
      <>
        <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#121212]">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-fuchsia-400" />
            <p className="text-white font-semibold text-sm">Messages</p>
            {totalUnread > 0 && <span className="px-1.5 py-0.5 bg-fuchsia-600 rounded-full text-[10px] text-white">{totalUnread}</span>}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-gray-400 hover:text-white h-8 w-8" data-testid="chat-close-button">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-fuchsia-600 border-t-transparent rounded-full animate-spin" /></div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-12">
              <MessageCircle className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Aucun message</p>
            </div>
          ) : conversations.map((conv) => (
            <button
              key={conv.conversation_id}
              onClick={() => openConversation(conv.conversation_id)}
              className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left border-b border-white/5"
              data-testid={`widget-conv-${conv.conversation_id}`}
            >
              {conv.user_picture ? (
                <img src={conv.user_picture} alt="" className="w-9 h-9 rounded-full flex-shrink-0" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-fuchsia-500/20 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-fuchsia-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-white text-sm font-medium truncate">{conv.user_name}</p>
                  <span className="text-gray-500 text-[10px] flex-shrink-0">{formatDate(conv.last_at)}</span>
                </div>
                <div className="flex items-center justify-between mt-0.5">
                  <p className="text-gray-500 text-xs truncate">{conv.last_sender_role === 'admin' ? 'Vous: ' : ''}{conv.last_message}</p>
                  {conv.unread_count > 0 && <span className="w-4 h-4 bg-fuchsia-600 rounded-full text-[10px] flex items-center justify-center text-white flex-shrink-0 ml-1">{conv.unread_count}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      </>
    );
  }

  // Conversation view
  return (
    <>
      <div className="flex items-center gap-2 p-4 border-b border-white/10 bg-[#121212]">
        <button onClick={() => setSelectedConv(null)} className="text-gray-400 hover:text-white" data-testid="chat-back-button">
          <ArrowLeft className="w-5 h-5" />
        </button>
        {selectedConvInfo?.user_picture ? (
          <img src={selectedConvInfo.user_picture} alt="" className="w-7 h-7 rounded-full" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-fuchsia-500/20 flex items-center justify-center">
            <User className="w-3.5 h-3.5 text-fuchsia-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium truncate">{selectedConvInfo?.user_name}</p>
          <p className="text-gray-500 text-[10px] truncate">{selectedConvInfo?.user_email}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="text-gray-400 hover:text-white h-8 w-8">
          <X className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="admin-widget-messages">
        {messages.map((msg) => {
          const isAdmin = msg.sender_role === 'admin';
          return (
            <div key={msg.message_id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-2xl ${isAdmin ? 'bg-fuchsia-600 text-white rounded-br-sm' : 'bg-white/10 text-gray-200 rounded-bl-sm'}`}>
                <p className="text-sm leading-relaxed break-words">{msg.message}</p>
                <p className={`text-[10px] mt-1 ${isAdmin ? 'text-white/60' : 'text-gray-500'} text-right`}>{formatTime(msg.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={handleReply} className="p-3 border-t border-white/10 bg-[#121212]">
        <div className="flex gap-2">
          <input ref={inputRef} type="text" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Répondre..." className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-fuchsia-500/50" data-testid="admin-widget-reply-input" />
          <Button type="submit" disabled={!reply.trim() || sending} className="bg-fuchsia-600 hover:bg-fuchsia-500 rounded-xl px-3 disabled:opacity-40" data-testid="admin-widget-send-button">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </form>
    </>
  );
};

// ==================== GUEST VIEW (not logged in) ====================
const GuestView = ({ onClose }) => {
  const navigate = useNavigate();
  return (
    <>
      <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#121212]">
        <div className="flex items-center gap-3">
          <img src="https://customer-assets.emergentagent.com/job_fad4c604-2efd-4142-a35f-93aeb2be7f74/artifacts/hzrybxar_logo2.jpg" alt="PG" className="w-9 h-9 rounded-full object-cover" />
          <div>
            <p className="text-white font-semibold text-sm">Phoenix Card Game</p>
            <p className="text-green-400 text-xs">En ligne</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="text-gray-400 hover:text-white h-8 w-8" data-testid="chat-close-button">
          <X className="w-4 h-4" />
        </Button>
      </div>
      <div className="p-6 text-center">
        <MessageCircle className="w-10 h-10 text-fuchsia-400 mx-auto mb-4" />
        <p className="text-white font-medium mb-2">Besoin d'aide ?</p>
        <p className="text-gray-400 text-sm mb-5">Connectez-vous pour discuter avec notre équipe.</p>
        <Button
          onClick={() => { onClose(); navigate('/login'); }}
          className="bg-fuchsia-600 hover:bg-fuchsia-500 w-full"
          data-testid="chat-login-button"
        >
          <LogIn className="w-4 h-4 mr-2" /> Se connecter
        </Button>
      </div>
    </>
  );
};

// ==================== MAIN CHAT WIDGET ====================
export const ChatWidget = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [windowBottom, setWindowBottom] = useState(12);
  const isAdmin = user?.role === 'admin';

  const fetchUnread = useCallback(async () => {
    if (!user) return;
    try {
      if (isAdmin) {
        const res = await axios.get(`${API}/admin/chat/conversations`, { withCredentials: true });
        const total = res.data.conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
        setUnreadCount(total);
      } else {
        const res = await axios.get(`${API}/chat/unread-count`, { withCredentials: true });
        setUnreadCount(res.data.count);
      }
    } catch { /* ignore */ }
  }, [isAdmin]);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  useEffect(() => {
    if (open) setUnreadCount(0);
  }, [open]);

  // Keep chat pinned to visual viewport bottom (prevents keyboard push on mobile)
  useEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => {
      const offset = window.innerHeight - vv.height - vv.offsetTop;
      setWindowBottom(12 + Math.max(0, offset));
    };
    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onResize);
      setWindowBottom(12);
    };
  }, [open]);

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-3 right-3 z-[99999] flex items-center gap-3 bg-[#121212] border border-white/10 rounded-full pl-5 pr-3 py-2.5 shadow-lg shadow-black/40 hover:border-fuchsia-500/40 transition-all group cursor-text"
          data-testid="chat-widget-button"
        >
          <span className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors">Contactez nous</span>
          <div className="w-9 h-9 rounded-full bg-fuchsia-600 flex items-center justify-center flex-shrink-0 relative">
            <MessageCircle className="w-4 h-4 text-white" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] flex items-center justify-center text-white font-medium animate-pulse" data-testid="chat-unread-badge">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
        </button>
      )}

      {open && (
        <div
          className="fixed right-3 z-[99999] w-[360px] bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          style={{ bottom: `${windowBottom}px`, height: user ? '500px' : 'auto' }}
          data-testid="chat-window"
        >
          {!user ? (
            <GuestView onClose={() => setOpen(false)} />
          ) : isAdmin ? (
            <AdminChatView user={user} onClose={() => setOpen(false)} />
          ) : (
            <UserChatView user={user} onClose={() => setOpen(false)} />
          )}
        </div>
      )}
    </>
  );
};
